import { Toaster as Sonner, toast } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  useRoutes,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { routes } from "@/routes";
import { Bounce, ToastContainer } from "react-toastify";
import { useEffect } from "react";

import { setupGlobalFetchInterceptor } from "./interceptor/setupGlobalFetch";
import OfflinePage from "./components/ui/OfflinePage";

import { setupForegroundNotifications } from "./utils/notifications/setupForegroundNotifications";

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // if (!isAuthenticated) {
  //   return <Navigate to="/login" replace />;
  // }

  return <>{children}</>;
};

const AppRoutes = () => {
  return useRoutes(routes);
};

const InterceptorSetup = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.onerror = function (msg, url, line, col, error) {
      console.log(msg, url, line, col, error);
    };

    setupGlobalFetchInterceptor();
  }, [navigate]);

  return <>{children}</>;
};

const AppInitializer = () => {
  useEffect(() => {
    setupForegroundNotifications();
  }, []);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <AppInitializer />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
          transition={Bounce}
        />

        <Sonner />

        <BrowserRouter
          future={{
            v7_startTransition: true,
          }}
        >
          <OfflinePage />

          <InterceptorSetup>
            <AppRoutes />
          </InterceptorSetup>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
